/**
 * Google Consent Mode v2 — wymóg Google Ads / EEA od marca 2024.
 *
 * Domyślnie wszystkie kategorie ustawione na "denied" PRZED jakimkolwiek hitem.
 * Po wyborze użytkownika wywołujemy gtag('consent','update', ...).
 *
 * Kategorie:
 * - necessary  — zawsze granted (techniczne, np. sesja)
 * - analytics  — GA4, Clarity (analytics_storage)
 * - marketing  — Google Ads, Facebook Pixel (ad_*, personalization_storage)
 */

export const CONSENT_KEY = "synctech_cookie_consent_v2";
export const CONSENT_EVENT = "synctech:consent-changed";

export type ConsentCategory = "analytics" | "marketing";

export type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: string; // ISO timestamp wyboru
  v: 2; // wersja schematu
};

export const DEFAULT_DENIED: Omit<ConsentState, "ts"> = {
  necessary: true,
  analytics: false,
  marketing: false,
  v: 2,
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/** Ustawia gtag stub + default consent = denied. Wywołać RAZ jak najwcześniej. */
export function initConsentMode(): void {
  if (typeof window === "undefined") return;
  if ((window as any).__synctechConsentInit) return;
  (window as any).__synctechConsentInit = true;

  window.dataLayer = window.dataLayer || [];
  // gtag musi być function declaration korzystającym z arguments — tak wymaga Google
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });

  // SyncTech działa w PL/EEA — region narzucony bezwarunkowo
  window.gtag("set", "ads_data_redaction", true);
  window.gtag("set", "url_passthrough", true);
}

export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed?.v !== 2) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(partial: { analytics: boolean; marketing: boolean }): ConsentState {
  const state: ConsentState = {
    necessary: true,
    analytics: partial.analytics,
    marketing: partial.marketing,
    ts: new Date().toISOString(),
    v: 2,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
  } catch {
    // ignore (Safari private mode)
  }
  applyConsent(state);
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
  } catch {
    // ignore
  }
  return state;
}

/** Wysyła consent update do Google + zwykły event do dataLayer. */
export function applyConsent(state: ConsentState): void {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") initConsentMode();

  window.gtag("consent", "update", {
    ad_storage: state.marketing ? "granted" : "denied",
    ad_user_data: state.marketing ? "granted" : "denied",
    ad_personalization: state.marketing ? "granted" : "denied",
    analytics_storage: state.analytics ? "granted" : "denied",
  });

  window.dataLayer.push({
    event: "synctech_consent_update",
    consent: {
      analytics: state.analytics,
      marketing: state.marketing,
    },
  });
}
